'use client';

import { useCallback, useState } from 'react';

import { addRecentSearch, clearRecentSearches, loadRecentSearches, removeRecentSearch } from '@/utils/search/recentSearchStorage';

/**
 * The user's past search terms.
 *
 * Every mutation takes the next list from the storage layer rather than
 * computing its own, so state and `localStorage` can't drift into showing a
 * term that won't survive a reload.
 *
 * Read lazily, in the initializer: the list only exists in the browser, and
 * reading it during module evaluation would run on the server.
 */
const useRecentSearches = () => {
  const [recentSearches, setRecentSearches] = useState<string[]>(loadRecentSearches);

  const remember = useCallback((term: string) => {
    if (!term) return;
    setRecentSearches(addRecentSearch(term));
  }, []);

  const forget = useCallback((term: string) => setRecentSearches(removeRecentSearch(term)), []);

  const forgetAll = useCallback(() => setRecentSearches(clearRecentSearches()), []);

  return { recentSearches, remember, forget, forgetAll };
};

export default useRecentSearches;
